import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { ArrowLeft, ArrowRight, CalendarClock, History, LockKeyhole, PoundSterling, RotateCcw, Wrench } from "lucide-react";
import { toast } from "sonner";
import { AppHeader } from "@/components/site/AppHeader";
import { Button } from "@/components/ui/button";
import { gbp, ukDate } from "@/lib/format";
import {
  getManagedHomeOverview,
  listManagedHomes,
  prepareRepeatHomeBooking,
} from "@/lib/homes.functions";

export const Route = createFileRoute("/_authenticated/home/history")({
  validateSearch: (search: Record<string, unknown>) => ({ homeId: typeof search['homeId'] === "string" ? search['homeId'] : undefined }),
  head: () => ({
    meta: [
      { title: "Home history — Shields" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: HomeHistoryPage,
});

function HomeHistoryPage() {
  const { homeId: requestedHomeId } = Route.useSearch();
  const navigate = useNavigate();
  const homes = useQuery({ queryKey: ["managed-homes"], queryFn: () => listManagedHomes() });
  const [selectedId, setSelectedId] = useState("");
  const homeId = selectedId || homes.data?.find((home) => home.id === requestedHomeId)?.id || homes.data?.[0]?.id || "";
  const overview = useQuery({
    queryKey: ["managed-home-overview", homeId],
    queryFn: () => getManagedHomeOverview({ data: { homeId } }),
    enabled: Boolean(homeId),
  });

  const data = overview.data?.ok ? overview.data : null;
  const completed = data?.completed ?? [];
  const upcoming = data?.upcoming ?? [];
  const previousPartners = data?.previousPartners ?? [];

  async function bookAgain(bookingId: string) {
    const result = await prepareRepeatHomeBooking({ data: { bookingId } });
    if (!result.ok) {
      toast.error(result.error);
      return;
    }
    toast.success("Shields will prioritise your previous Partner for this repeat request");
    navigate({
      to: "/book",
      search: { ...(result.serviceSlug ? { service: result.serviceSlug } : {}), homeId },
    });
  }

  return (
    <div className="min-h-screen bg-[#f7f9fc] pb-24">
      <AppHeader workspace="customer" />
      <main className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <Link to="/home" className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground">
          <ArrowLeft className="h-4 w-4" /> My Home
        </Link>

        <header className="mt-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#c99522]">My Home</p>
          <h1 className="mt-1 text-3xl font-extrabold text-[#061b3a]">Service History</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            A simple record of completed work, dates, prices and the Partners you used.
          </p>
        </header>

        {(homes.data ?? []).length > 1 && (
          <label className="mt-6 grid max-w-xl gap-1.5 text-sm font-semibold">
            Property
            <select
              value={homeId}
              onChange={(event) => setSelectedId(event.target.value)}
              className="min-h-12 rounded-xl border border-input bg-background px-3"
            >
              {(homes.data ?? []).map((home) => (
                <option key={home.id} value={home.id}>
                  {home.label} · {home.postcode}{home.isShared ? " · shared with you" : ""}
                </option>
              ))}
            </select>
          </label>
        )}

        {!homeId ? (
          <section className="card-surface mt-8 p-6">
            <h2 className="font-display text-lg font-bold">Add your first property</h2>
            <p className="mt-2 text-sm text-muted-foreground">A saved property gives Shields somewhere to keep service history and home records.</p>
            <Link to="/home" className="btn-hero mt-4 inline-flex">Open My Home</Link>
          </section>
        ) : overview.isLoading ? (
          <p className="mt-8 text-sm text-muted-foreground">Loading property history…</p>
        ) : !data ? (
          <section className="card-surface mt-8 p-6">
            <h2 className="font-display text-lg font-bold">We couldn’t load this property history</h2>
            <p className="mt-2 text-sm text-muted-foreground">Try again. Your saved home and service records have not been changed.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" onClick={() => overview.refetch()} className="btn-secondary-hero">
                Try again
              </button>
              <Link to="/home" className="btn-secondary-hero">Back to My Home</Link>
            </div>
          </section>
        ) : (
          <>
            <section className="mt-7 grid gap-3 sm:grid-cols-3">
              <article className="os-metric-card">
                <span className="knowledge-state knowledge-state-success">Recorded</span>
                <strong>{completed.length} completed job{completed.length === 1 ? "" : "s"}</strong>
                <small className="text-muted-foreground">Real Shields work attached to this property.</small>
              </article>
              <article className="os-metric-card">
                <span className="knowledge-state knowledge-state-info">Upcoming</span>
                <strong>{upcoming.length} active job{upcoming.length === 1 ? "" : "s"}</strong>
                <small className="text-muted-foreground">Current and scheduled Shields work.</small>
              </article>
              <article className="os-metric-card">
                <span className="knowledge-state knowledge-state-info">Previous Partners</span>
                <strong>{previousPartners.length}</strong>
                <small className="text-muted-foreground">Professionals used at this property.</small>
              </article>
            </section>

            <section className="mt-7">
              <div className="flex items-center gap-2"><CalendarClock className="h-5 w-5 text-primary" /><h2 className="text-lg font-extrabold">Upcoming & active</h2></div>
              {upcoming.length === 0 ? <p className="mt-4 text-sm text-muted-foreground">No active Shields work for this property.</p> : (
                <div className="reference-rows mt-4">
                  {upcoming.map((booking: any) => (
                    <Link key={booking.id} to="/bookings/$bookingId" params={{ bookingId: booking.id }} className="reference-row">
                      <span className="reference-row-icon"><Wrench /></span>
                      <span>
                        <strong>{booking.services?.name ?? "Service"}</strong>
                        <small>{String(booking.status).replaceAll("_", " ")}{booking.scheduled_date ? " · " + ukDate(booking.scheduled_date) : ""}</small>
                      </span>
                      <ArrowRight />
                    </Link>
                  ))}
                </div>
              )}
            </section>

            <section className="dashboard-section">
              <div className="flex items-center gap-2"><History className="h-5 w-5 text-primary" /><h2 className="text-lg font-extrabold">Completed service history</h2></div>
              {completed.length === 0 ? <p className="mt-4 text-sm text-muted-foreground">Completed Shields jobs at this property will appear here automatically.</p> : (
                <div className="mt-4 space-y-3">
                  {completed.map((booking: any) => (
                    <article key={booking.id} className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-[#e1e8f0] bg-white p-4">
                      <div>
                        <strong>{booking.services?.name ?? "Service"}</strong>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {booking.completed_at ? ukDate(booking.completed_at) : ukDate(booking.created_at)}
                          {booking.partner?.trading_name ? " · " + booking.partner.trading_name : ""}
                          {booking.agreed_price_pence != null ? " · " + gbp(booking.agreed_price_pence) : ""}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Link to="/bookings/$bookingId" params={{ bookingId: booking.id }} className="inline-flex min-h-10 items-center rounded-lg border border-border px-3 text-sm font-bold">View</Link>
                        {data.access === "owner" && booking.assigned_partner_id && (
                          <Button type="button" size="sm" onClick={() => bookAgain(booking.id)}>
                            <RotateCcw className="mr-1 h-4 w-4" /> Book again
                          </Button>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <section className="dashboard-section">
              <div className="flex items-center gap-2"><PoundSterling className="h-5 w-5 text-primary" /><h2 className="text-lg font-extrabold">Property spending</h2></div>
              {data.spending.entitled ? (
                <div className="mt-4 card-surface p-5">
                  <p className="text-sm text-muted-foreground">Recorded agreed prices for completed Shields work</p>
                  <strong className="mt-1 block text-3xl text-[#07164f]">{gbp(data.spending.totalPence)}</strong>
                  <p className="mt-1 text-xs text-muted-foreground">Based only on jobs recorded in Shields. It is not an estimate of work completed elsewhere.</p>
                </div>
              ) : (
                <div className="business-locked mt-4">
                  <LockKeyhole />
                  <div>
                    <h3>Home spending insights — Family</h3>
                    <p>See the recorded Shields spend attached to each property.</p>
                    <Link to="/plans">Compare memberships</Link>
                  </div>
                </div>
              )}
            </section>

            {previousPartners.length > 0 && (
              <section className="dashboard-section">
                <h2 className="text-lg font-extrabold">Previous Partners</h2>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  {previousPartners.map((partner: any) => (
                    <article key={partner.partnerId} className="card-surface p-4">
                      <strong>{partner.tradingName}</strong>
                      <p className="mt-1 text-sm text-muted-foreground">{partner.serviceName}</p>
                      {data.access === "owner" && (
                        <Button type="button" size="sm" className="mt-3" onClick={() => bookAgain(partner.lastBookingId)}>
                          Book again
                        </Button>
                      )}
                    </article>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}
